<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { database } from '@/firebase/config'
import { ref as dbRef, get, off } from 'firebase/database'
import { ICertificate, IDegree } from '@shared/types'
import { EIdNivelEstudios } from '@shared/types/base'
import BaseModal from '@/components/BaseModal.vue'
import dateControl from '@/components/utils/dateControl.vue'
import { EEstatusDocumento, ENivelEstudio, ETipoDocumento } from '@shared/types/enums'
import { formatDate } from '../utils/dateFormatter'

interface DateRangeFilter {
	from: number | null
	to: number | null
}
interface TPackage {
	id: string
	type: 'certificates' | 'degrees'
	zipBase64: string
	label?: string
	date?: number
	responsables?: {
		[key: string]: any
	}
	sentData?: {
		cargaTituloElectronico: {
			mensaje: string
			numeroLote: string
		}
		descargaTituloElectronico: {
			mensaje: string
			numeroLote: string
			titulosBase64: string
		}
		consultaProcesoTituloElectronico: {
			mensaje: string
			numeroLote: string
			estatusLote: number
		}
	}
}

const store = useStore()
const db = database

//ESTO si
const certificatesList = ref<ICertificate[]>([])
const degrees = ref<IDegree[]>([])
const packages = ref<TPackage[]>([])
const currentInstitution = computed(() => store.getters['global/currentInstitution'])
const loading = ref(false)
const showErrorModal = ref(false)
const errorMessage = ref<string>('')
const dateFrom = ref<number | null>(null)
const dateTo = ref<number | null>(null)

const updateDateFrom = (date: number) => {
	dateFrom.value = date
}

const updateDateTo = (date: number) => {
	dateTo.value = date
}

const isPackageInDateRange = (packageId: string, range: DateRangeFilter): boolean => {
	const pkgDate = new Date(packageId)
	if (Number.isNaN(pkgDate.getTime())) return false

	const pkgTime = pkgDate.getTime()

	if (range.from != null) {
		const fromStart = new Date(range.from)
		fromStart.setHours(0, 0, 0, 0)
		if (pkgTime < fromStart.getTime()) return false
	}

	if (range.to != null) {
		const toEnd = new Date(range.to)
		toEnd.setHours(23, 59, 59, 999)
		if (pkgTime > toEnd.getTime()) return false
	}

	return true
}

// Firebase paths
const getInstitutionPath = (path: string) => `/institutions/${currentInstitution.value}${path}`

const loadPackagesCertificates = async (dateRange: DateRangeFilter) => {
	// Clear previous listeners
	const packagesRef = dbRef(db, getInstitutionPath('/certificates'))
	off(packagesRef)

	return new Promise<void>((resolve, reject) => {
		get(packagesRef)
			.then(async (snapshot: any) => {
				const data = snapshot.val()
				if (data) {
					const queuePackages = data.packages
					const packagesCertificates = getSignedPackagesInDateRange(queuePackages, dateRange)
					await getCertificates(packagesCertificates)
				} else {
					packages.value = []
				}
				resolve()
			})
			.catch((error: any) => {
				console.error('Error loading packages:', error)
				packages.value = []
				reject(error)
			})
	})
}
const loadPackagesDegrees = async (dateRange: DateRangeFilter) => {
	// Clear previous listeners
	const packagesRef = dbRef(db, getInstitutionPath('/degrees'))
	off(packagesRef)

	return new Promise<void>((resolve, reject) => {
		get(packagesRef)
			.then(async (snapshot: any) => {
				const data = snapshot.val()
				if (data) {
					const queuePackages = data.packages
					const packagesDegrees = getSignedPackagesInDateRange(queuePackages, dateRange)
					await getDegrees(packagesDegrees)
				} else {
					packages.value = []
				}
				resolve()
			})
			.catch((error: any) => {
				console.error('Error loading packages:', error)
				packages.value = []
				reject(error)
			})
	})
}
const getSignedPackagesInDateRange = (queuePackages: TPackage[], dateRange: DateRangeFilter) =>
	Object.entries(queuePackages)
		.map(([id, pkg]: [string, any]) => ({ id, ...pkg }))
		.filter((pkg: any) => pkg.sign === true)
		.filter((pkg: any) => isPackageInDateRange(pkg.id, dateRange))
const hasDocumentError = (document: { error?: { message?: string } }) => Boolean(document.error?.message)

const getCertificates = async (packagesCertificates: any[]) => {
	const results: ICertificate[] = []
	const fetches: Promise<void>[] = []

	for (const pkg of packagesCertificates) {
		const certificatesPackages: Record<string, boolean> = pkg?.certificates || {}
		for (const certificate of Object.keys(certificatesPackages)) {
			const certificatesRef = dbRef(db, getInstitutionPath(`/certificates/queue/${certificate}`))
			off(certificatesRef)
			fetches.push(
				get(certificatesRef).then((snapshot: any) => {
					if (!snapshot.exists()) return
					const item = { id: snapshot.key, ...snapshot.val() } as ICertificate
					if (!hasDocumentError(item)) {
						results.push(item)
					}
				})
			)
		}
	}

	await Promise.all(fetches)
	certificatesList.value.push(...results)
}
const getDegrees = async (packagesDegrees: any[]) => {
	const results: IDegree[] = []
	const fetches: Promise<void>[] = []

	for (const pkg of packagesDegrees) {
		const degreesPackages: Record<string, boolean> = pkg?.degrees || {}
		for (const degree of Object.keys(degreesPackages)) {
			const degreesRef = dbRef(db, getInstitutionPath(`/degrees/queue/${degree}`))
			off(degreesRef)
			fetches.push(
				get(degreesRef).then((snapshot: any) => {
					if (!snapshot.exists()) return
					const item = { id: snapshot.key, ...snapshot.val() } as IDegree
					if (!hasDocumentError(item)) {
						results.push(item)
					}
				})
			)
		}
	}

	await Promise.all(fetches)
	degrees.value.push(...results)
}

// Initialize
onMounted(async () => {
	// Make sure we have the institution ID before loading certificates
	if (!currentInstitution.value) {
		try {
			// Try to load institutions if not already loaded
			await store.dispatch('global/loadInstitutions', null, { root: true })

			// Check again if we have an institution ID
			if (!currentInstitution.value) {
				errorMessage.value = 'No se pudo determinar la institución. Por favor, seleccione una institución.'
				showErrorModal.value = true
				return
			}
		} catch (error) {
			console.error('Error loading institutions:', error)
			errorMessage.value = 'Error al cargar las instituciones. Por favor, intente nuevamente.'
			showErrorModal.value = true
			return
		}
	}

	// Cleanup function
	return () => {
		// Limpiar listeners al desmontar el componente
		const certificatesRef = dbRef(db, getInstitutionPath('/certificates'))
		const packagesRef = dbRef(db, getInstitutionPath('/certificates'))
		off(certificatesRef)
		off(packagesRef)
	}
})

const searchDocuments = async () => {
	loading.value = true
	const dateRange: DateRangeFilter = { from: dateFrom.value, to: dateTo.value }
	if (dateRange.from == null || dateRange.to == null) {
		errorMessage.value = 'Por favor, seleccione una fecha de inicio y fin.'
		showErrorModal.value = true
		loading.value = false
		return
	}
	certificatesList.value = []
	degrees.value = []
	Promise.all([loadPackagesCertificates(dateRange), loadPackagesDegrees(dateRange)]).finally(() => {
		loading.value = false
	})
}
function normalizeNumberValue(raw: unknown): number | null {
	if (raw == null || raw === '') return null
	const id = typeof raw === 'number' ? raw : Number.parseInt(String(raw).trim(), 10)
	return Number.isFinite(id) ? id : null
}
const TIPO_DOCUMENTO_TITULO_POR_NIVEL: Record<number, ETipoDocumento> = {
	[ENivelEstudio.LICENCIATURA]: ETipoDocumento.TITULO_PROFESIONAL,
	[ENivelEstudio.MAESTRIA]: ETipoDocumento.DIPLOMA,
	[ENivelEstudio.PROFESIONAL_ASOCIADO]: ETipoDocumento.TITULO_PROFESIONAL,
	[ENivelEstudio.TECNICO_SUPERIOR_UNIVERSITARIO]: ETipoDocumento.GRADO,
	[ENivelEstudio.ESPECIALIDAD]: ETipoDocumento.DIPLOMA,
	[ENivelEstudio.DOCTORADO]: ETipoDocumento.GRADO
}

const TIPO_DOCUMENTO_POR_NOMBRE: Record<string, ETipoDocumento> = {
	LICENCIATURA: ETipoDocumento.TITULO_PROFESIONAL,
	MAESTRIA: ETipoDocumento.DIPLOMA,
	MAESTRÍA: ETipoDocumento.DIPLOMA,
	PROFESIONAL_ASOCIADO: ETipoDocumento.TITULO_PROFESIONAL,
	PROFESIONAL: ETipoDocumento.TITULO_PROFESIONAL,
	TECNICO_SUPERIOR_UNIVERSITARIO: ETipoDocumento.GRADO,
	TÉCNICO: ETipoDocumento.GRADO,
	ESPECIALIDAD: ETipoDocumento.DIPLOMA,
	DOCTORADO: ETipoDocumento.GRADO
}

const getTipoDocumentoTitulo = (idNivelEstudios: unknown, nombreCarrera: string): string => {
	const id = normalizeNumberValue(idNivelEstudios)
	if (id != null) {
		console.log('Nombre de la carrera:', nombreCarrera)
		return TIPO_DOCUMENTO_TITULO_POR_NIVEL[id] ?? EIdNivelEstudios[String(id) as keyof typeof EIdNivelEstudios] ?? ''
	}

	const identifier = (nombreCarrera?.split(' ')[0] ?? '').toUpperCase()
	return TIPO_DOCUMENTO_POR_NOMBRE[identifier] ?? ''
}

const generateTemplate = async () => {
	const XLSX = await import('xlsx')

	// Encabezados del Excel ESTATUS,AÑO ESCOLAR, CURP, PROMEDIO, TIPO DOCUMENTO, FECHA DE EMISION, FOLIO DEL DOCUMENTO
	const headers = ['Estatus', 'Año del ciclo escolar', 'CURP', 'Promedio general', 'Tipo de documento', 'Fecha de expedición del documento', 'Folio del documento']
	const rows: any[][] = [headers]

	// Función auxiliar para obtener CURPs de responsables

	// Función auxiliar para formatear fecha
	const formatDate = (date: number | string | undefined): string => {
		if (!date) return ''
		const dateObj = new Date(date)
		return dateObj.toLocaleDateString('es-MX', {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit'
		})
	}

	// Agregar certificados de producción
	for (const certificate of certificatesList.value) {
		const curp = certificate.data?.Alumno.curp
		rows.push([
			EEstatusDocumento.CERTIFICADO_ESTUDIOS,
			new Date(certificate.package).toLocaleDateString().split('/')[2],
			curp,
			certificate.data?.Asignaturas.promedio,
			certificate.data?.Expedicion.tipoCertificacion === 'TOTAL' ? ETipoDocumento.CERTIFICADO_TOTAL : ETipoDocumento.CERTIFICADO_PARCIAL,
			formatDate(certificate.data?.Expedicion.fecha),
			''
		])
	}

	// Agregar títulos de producción
	for (const degree of degrees.value) {
		rows.push([
			EEstatusDocumento.TITULO_PROFESIONAL,
			new Date(degree.package).toLocaleDateString().split('/')[2],
			degree.data?.Profesionista.curp,
			'',
			getTipoDocumentoTitulo(degree.data?.Carrera.idNivelEstudios, degree.data?.Carrera.nombreCarrera),
			formatDate(degree.data?.Expedicion.fechaExpedicion),
			''
		])
	}

	// Crear el archivo Excel
	const ws = XLSX.utils.aoa_to_sheet(rows)
	applyWorksheetLayout(XLSX, ws, rows)
	const wb = XLSX.utils.book_new()
	XLSX.utils.book_append_sheet(wb, ws, 'Anexo 9')
	// Generar nombre de archivo con fecha actual
	const fechaActual = new Date().toISOString().split('T')[0]
	XLSX.writeFile(wb, `anexo9_${fechaActual}.xlsx`)
}

const buildWorksheetColumnWidths = (rows: unknown[][], minWidth = 12, maxWidth = 48) => {
	const columnCount = rows.reduce((max, row) => Math.max(max, row.length), 0)
	const widths = Array.from({ length: columnCount }, () => minWidth)

	for (const row of rows) {
		row.forEach((cell, columnIndex) => {
			const contentLength = String(cell ?? '').length
			widths[columnIndex] = Math.min(maxWidth, Math.max(widths[columnIndex], contentLength + 2))
		})
	}

	return widths.map(wch => ({ wch }))
}

const applyWorksheetLayout = (XLSX: typeof import('xlsx'), ws: Record<string, unknown>, rows: unknown[][]) => {
	ws['!cols'] = buildWorksheetColumnWidths(rows)
	ws['!rows'] = [{ hpt: 24 }]

	const ref = ws['!ref'] as string | undefined
	if (!ref) return

	const range = XLSX.utils.decode_range(ref)
	ws['!autofilter'] = {
		ref: XLSX.utils.encode_range({
			s: { r: 0, c: 0 },
			e: { r: range.e.r, c: range.e.c }
		})
	}
	ws['!freeze'] = {
		xSplit: 0,
		ySplit: 1,
		topLeftCell: 'A2',
		activePane: 'bottomLeft',
		state: 'frozen'
	}
}
</script>
<template>
	<VaCard square bordered style="padding: 2rem !important">
		<VaCardTitle class="anexo9-card-title">
			<div class="anexo9-header__controls">
				<div class="anexo9-header__search-zone">
					<h2 class="anexo9-header__title">Anexo 9</h2>
					<p class="anexo9-header__subtitle">Consulta y exporta documentos emitidos</p>
				</div>
				<div class="anexo9-header__zone-divider" aria-hidden="true" />
				<div class="anexo9-header__export-zone">
					<span class="anexo9-header__zone-label">Exportar</span>
					<VaButton class="anexo9-header__btn anexo9-header__btn--primary" color="primary" :disabled="loading" @click="generateTemplate">
						<VaIcon name="download" class="button-icon" />
						Generar reporte
					</VaButton>
				</div>
			</div>
		</VaCardTitle>
		<VaCardContent>
			<div class="anexo9-header__controls">
				<span class="anexo9-header__zone-label">Filtrado por fecha</span>
				<div class="anexo9-header__search-row">
					<div class="anexo9-header__date row" style="background-color: white; width: 90%; margin-right: 10px">
						<dateControl class="col-md-6" :value="dateFrom" :disabled="false" :defaultValue="true" label="Fecha inicio" @update-Date="updateDateFrom" />
						<dateControl class="col-md-6" :value="dateTo" :disabled="false" :defaultValue="true" label="Fecha fin" @update-Date="updateDateTo" />
					</div>

					<VaButton class="anexo9-header__btn anexo9-header__btn--primary" color="primary" :disabled="loading" @click="searchDocuments">
						<VaIcon name="search" class="button-icon" />
						Buscar documentos
					</VaButton>
				</div>
			</div>
			<div class="package-grid last-section" style="margin: 0.5rem">
				<div class="package-card info-card">
					<div class="card-content-wrapper">
						<div class="info-header">
							<div class="info-icon-wrapper">
								<va-icon name="event" class="info-icon" />
							</div>
							<div class="card-title-group">
								<h3 class="card-title">Certificados emitidos en producción</h3>
								<p class="card-subtitle">Cantidad de certificados emitidos en producción</p>
							</div>
						</div>
						<div class="info-value">
							{{ certificatesList.length }}
						</div>
					</div>
				</div>

				<div class="package-card info-card">
					<div class="card-content-wrapper">
						<div class="info-header">
							<div class="info-icon-wrapper">
								<va-icon name="event" class="info-icon" />
							</div>
							<div class="card-title-group">
								<h3 class="card-title">Títulos emitidos en producción</h3>
								<p class="card-subtitle">Cantidad de títulos emitidos en producción</p>
							</div>
						</div>
						<div class="info-value">
							{{ degrees.length }}
						</div>
					</div>
				</div>
			</div>
			<div class="package-grid last-section">
				<div class="package-card info-card">
					<div class="card-content-wrapper">
						<div class="info-header">
							<div class="info-icon-wrapper">
								<va-icon name="event" class="info-icon" />
							</div>
							<div class="card-title-group">
								<h3 class="card-title">Paquetes emitidos en producción</h3>
								<p class="card-subtitle">Cantidad de paquetes emitidos en producción</p>
							</div>
						</div>
						<div class="info-value">
							{{ packages.length }}
						</div>
					</div>
				</div>
			</div>
			<hr class="divider" style="color: #0d6efd" />
			<div class="content">
				<div v-if="loading" class="loading-container">
					<VaProgressCircle indeterminate color="primary" size="large" />
					<p class="loading-text">Cargando documentos...</p>
				</div>
				<div v-else-if="certificatesList.length === 0 && degrees.length === 0" class="empty-state">
					<img src="@/assets/icons/certificateIcon.svg" alt="No certificates" class="empty-icon" />
					<p class="empty-title">Aún no has creado certificados</p>
					<p class="empty-description">Crea un nuevo certificado o importa varios desde un archivo de excel</p>
				</div>
				<div v-else>
					<!-- Sección de Certificados: Producción y Prueba lado a lado -->
					<h3 class="section-title">Certificados</h3>
					<div>
						<div class="table-container">
							<div class="va-table-responsive">
								<table class="certificates-table">
									<thead>
										<tr>
											<th colspan="6" style="text-align: center" id="Certificados emitidos en producción">Certificados emitidos en producción</th>
										</tr>
										<tr>
											<th id="Estatus">Estatus</th>
											<th id="Año Escolar">Año Escolar</th>
											<th id="CURP">CURP</th>
											<th id="Promedio">Promedio</th>
											<th id="Tipo de documento">Tipo de documento</th>
											<th id="Fecha de expedición del documento">Fecha de expedición del documento</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="certificate in certificatesList" :key="certificate.id">
											<td>{{ EEstatusDocumento.CERTIFICADO_ESTUDIOS }}</td>
											<td>{{ new Date(certificate.package).toLocaleDateString().split('/')[2] }}</td>
											<td>{{ certificate.data.Alumno.curp }}</td>
											<td>{{ certificate.data.Asignaturas.promedio }}</td>
											<td>{{ certificate.data.Expedicion.tipoCertificacion === 'TOTAL' ? ETipoDocumento.CERTIFICADO_TOTAL : ETipoDocumento.CERTIFICADO_PARCIAL }}</td>
											<td>{{ formatDate(certificate.data.Expedicion.fecha) }}</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
					</div>

					<!-- Sección de Títulos: Producción y Prueba lado a lado -->
					<h3 class="section-title">Títulos</h3>
					<div>
						<div class="table-container">
							<div class="va-table-responsive">
								<table class="certificates-table">
									<thead>
										<tr>
											<th colspan="6" style="text-align: center" id="Títulos emitidos en producción">Títulos emitidos en producción</th>
										</tr>
										<tr>
											<th id="Estatus_titulo">Estatus</th>
											<th id="Año Escolar_titulo">Año Escolar</th>
											<th id="CURP_titulo">CURP</th>
											<th id="Tipo de documento_titulo">Tipo de documento</th>
											<th id="Fecha de expedición del documento_titulo">Fecha de expedición del documento</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="degree in degrees" :key="degree.id">
											<td>{{ EEstatusDocumento.TITULO_PROFESIONAL }}</td>
											<td>{{ new Date(degree.package).toLocaleDateString().split('/')[2] }}</td>
											<td>{{ degree.data.Profesionista.curp }}</td>
											<td>{{ getTipoDocumentoTitulo(degree.data.Carrera.idNivelEstudios, degree.data.Carrera.nombreCarrera) }}</td>
											<td>{{ formatDate(degree.data.Expedicion.fechaExpedicion) }}</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
			</div>
		</VaCardContent>
	</VaCard>
	<BaseModal v-model="showErrorModal" title="Error" :message="errorMessage" :testMode="false" size="medium">
		<template #body>
			<div class="error-message">
				<div class="error-header">
					<div class="error-icon">
						<i class="fas fa-exclamation-triangle"></i>
					</div>
					<h3 class="error-title">Error</h3>
				</div>
				<p class="error-description">{{ errorMessage }}</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showErrorModal = false">Entendido</button>
		</template>
	</BaseModal>
</template>
<style scoped>
.anexo9-card-title {
	display: block !important;
	width: 100% !important;
	max-width: none !important;
	padding: 0 !important;
	margin-bottom: 1.25rem;
	text-transform: none !important;
}

.anexo9-header__title {
	margin: 0;
	font-size: 3rem;
	font-weight: 900;
	color: #ffffff;
	line-height: 1.2;
	text-transform: none;
}

.anexo9-header__subtitle {
	margin: 0.35rem 0 0;
	font-size: 0.9rem;
	color: #ffffff;
	line-height: 1.4;
	text-transform: none;
}

.anexo9-header__controls {
	display: flex;
	align-items: stretch;
	width: 100%;
	min-width: 0;
	padding: 1rem 1.25rem;
	background: linear-gradient(135deg, #062342 0%, #1b3f67 100%);
	border: 1px solid #e2e8f0;
	border-radius: 12px;
	box-shadow: 0 4px 12px rgba(37, 99, 235, 0.25);
}

.anexo9-header__search-zone {
	flex: 1 1 auto;
	display: flex;
	flex-direction: column;
	justify-content: flex-end;
	gap: 0.5rem;
	min-width: 0;
	padding-right: 1.25rem;
}

.anexo9-header__search-row {
	display: flex;
	align-items: flex-end;
	gap: 0.75rem;
	width: 100%;
}

.anexo9-header__date {
	background: #ffffff;
	border-radius: 10px;
	padding: 0.25rem 0.75rem;
}

.anexo9-header__zone-divider {
	width: 1px;
	flex-shrink: 0;
	align-self: stretch;
	margin: 0 0.25rem;
	background: #ffffff;
}

.anexo9-header__export-zone {
	display: flex;
	flex-direction: column;
	justify-content: flex-end;
	gap: 0.5rem;
	flex-shrink: 0;
	padding-left: 0.25rem;
}

.anexo9-header__zone-label {
	font-size: 0.68rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.08em;
	color: #ffffff;
}

.anexo9-header__btn {
	flex: 0 0 auto;
	padding: 0.625rem 1rem !important;
	font-size: 0.9rem !important;
	font-weight: 600;
	border-radius: 10px;
	white-space: nowrap;
}

.anexo9-header__btn--primary {
	min-width: 11rem;
	justify-content: center;
	box-shadow: 0 4px 12px rgba(37, 99, 235, 0.25);
}

.anexo9-header__btn--primary:hover:not(:disabled) {
	transform: translateY(-1px);
	box-shadow: 0 6px 16px rgba(37, 99, 235, 0.32);
}

@media (max-width: 992px) {
	.anexo9-header__controls {
		flex-direction: column;
		gap: 1rem;
	}

	.anexo9-header__search-zone {
		padding-right: 0;
	}

	.anexo9-header__zone-divider {
		width: 100%;
		height: 1px;
		margin: 0;
	}

	.anexo9-header__export-zone {
		padding-left: 0;
	}

	.anexo9-header__btn--primary {
		width: 100%;
	}
}

@media (max-width: 576px) {
	.anexo9-header__search-row {
		flex-direction: column;
		align-items: stretch;
	}

	.anexo9-header__btn {
		width: 100%;
		justify-content: center;
	}
}

.error-description {
	font-size: 1.1rem;
	line-height: 1.6;
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
	border-left: 4px solid #f71d1d;
}
.error-message {
	background: linear-gradient(135deg, #ecbaba 0%, #e67a7a 100%);
	border: 2px solid #f71d1d;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 32px rgba(254, 178, 178, 0.3);
	animation: slideInError 0.4s ease-out;
}

.error-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
	padding-bottom: 1rem;
	border-bottom: 2px solid #f68484;
}

.error-icon {
	width: 48px;
	height: 48px;
	background: linear-gradient(135deg, #e67a7a 0%, #e67a7a 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 16px rgba(245, 101, 101, 0.4);
}

.error-icon i {
	color: white;
	font-size: 1.5rem;
	animation: pulse 2s infinite;
}

.error-title {
	margin: 0;
	color: #e67a7a;
	font-size: 1.5rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.content {
	background: white;
	border-radius: 8px;
	padding: 20px;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.empty-state {
	text-align: center;
	padding: 40px;
	color: #666;
}

/* Estilos para habilitar scroll en el contenedor principal */
:deep(.main-content) {
	overflow-y: auto !important;
	height: calc(100vh - 60px) !important;
	padding: 1rem !important;
}

/* Asegurar que el contenido tenga scroll */
:deep(.va-card) {
	max-height: none;
	overflow: visible;
}

/* Asegurar que las tablas tengan scroll horizontal si es necesario */
:deep(.va-data-table) {
	overflow-x: auto;
}

/* Estilos para el scroll personalizado */
:deep(.main-content)::-webkit-scrollbar {
	width: 8px;
}

:deep(.main-content)::-webkit-scrollbar-track {
	background: #f1f1f1;
	border-radius: 4px;
}

:deep(.main-content)::-webkit-scrollbar-thumb {
	background: #888;
	border-radius: 4px;
}

:deep(.main-content)::-webkit-scrollbar-thumb:hover {
	background: #555;
}

/* ===== ESTILOS PARA SECCIÓN DE BÚSQUEDA ===== */

.empty-icon {
	font-size: 4rem;
	color: #6c757d;
	margin-bottom: 1rem;
}

.empty-title {
	font-size: 1.5rem;
	font-weight: 600;
	color: #1a2a3a;
	margin-bottom: 0.5rem;
}

.empty-description {
	color: #6c757d;
	font-size: 1rem;
	margin: 0;
}

.loading-container {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 60px 20px;
	gap: 20px;
}

.loading-text {
	font-size: 1.1rem;
	color: #667eea;
	font-weight: 500;
	margin: 0;
}

/* Animaciones */
@keyframes fadeIn {
	from {
		opacity: 0;
		transform: scale(0.8);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

/* Responsive Design para Búsqueda */
@media (max-width: 768px) {
	.empty-icon {
		font-size: 3rem;
	}

	.empty-title {
		font-size: 1.25rem;
	}
}

/* Animaciones */
@keyframes fadeIn {
	from {
		opacity: 0;
		transform: scale(0.8);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

/* ===== ESTILOS PARA TABLA DE CERTIFICADOS ===== */
.section-title {
	font-size: 1.5rem;
	font-weight: 700;
	color: #1a2a3a;
	margin: 30px 0 20px 0;
	padding-bottom: 10px;
	border-bottom: 3px solid #667eea;
}

.table-container {
	max-height: 600px;
	overflow-y: auto;
	overflow-x: auto;
	border-radius: 8px;
	border: 1px solid #e0e0e0;
	background: white;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
	flex: 1;
}

.table-container::-webkit-scrollbar {
	width: 10px;
	height: 10px;
}

.table-container::-webkit-scrollbar-track {
	background: #f5f5f5;
	border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb {
	background: #c1c1c1;
	border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb:hover {
	background: #a8a8a8;
}

.certificates-table {
	width: 100%;
	border-collapse: collapse;
	font-size: 0.95rem;
	background: white;
}

.certificates-table thead {
	position: sticky;
	top: 0;
	z-index: 10;
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
	color: white;
}

.certificates-table thead th {
	padding: 16px 20px;
	text-align: left;
	font-weight: 600;
	font-size: 0.9rem;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	border-bottom: 2px solid rgba(255, 255, 255, 0.2);
	white-space: nowrap;
}

.certificates-table tbody tr {
	border-bottom: 1px solid #e8e8e8;
	transition: all 0.2s ease;
}

.certificates-table tbody tr:hover {
	background-color: #f8f9ff;
	transform: scale(1.01);
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.certificates-table tbody tr:nth-child(even) {
	background-color: #fafafa;
}

.certificates-table tbody tr:nth-child(even):hover {
	background-color: #f8f9ff;
}

.certificates-table tbody td {
	padding: 14px 20px;
	color: #333;
	vertical-align: middle;
}

.certificates-table tbody td:first-child {
	font-weight: 600;
	color: #062342;
}

@media (max-width: 768px) {
	.section-title {
		font-size: 1.25rem;
		margin: 20px 0 15px 0;
	}

	.table-container {
		max-height: 400px;
	}

	.certificates-table {
		font-size: 0.85rem;
	}

	.certificates-table thead th,
	.certificates-table tbody td {
		padding: 10px 12px;
	}
}

/* ====================================================== */
/* ====================== ESTILOS ====================== */
/* ====================================================== */

.last-section {
	margin-bottom: 0;
}
/* Grid de tarjetas */
.package-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
	gap: 1.5rem;
}

/* Tarjetas base */
.package-card {
	position: relative;
	background: white;
	border-radius: 16px;
	padding: 0;
	transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
	overflow: hidden;
	animation: fadeInScale 0.6s ease-out;
	border: 1px solid rgba(0, 0, 0, 0.05);
}

.package-card::before {
	content: '';
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	height: 4px;
	background: linear-gradient(90deg, transparent 0%, rgba(102, 126, 234, 0.5) 50%, transparent 100%);
	opacity: 0;
	transition: opacity 0.3s ease;
}

.package-card:hover {
	transform: translateY(-8px) scale(1.02);
	box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

.package-card:hover::before {
	opacity: 1;
}

.card-content-wrapper {
	position: relative;
	padding: 1.75rem;
	z-index: 1;
}
.card-title-group {
	flex: 1;
}

.card-title {
	font-size: 1.125rem;
	font-weight: 700;
	color: #1a202c;
	margin: 0 0 0.25rem 0;
	letter-spacing: -0.3px;
}

.card-subtitle {
	font-size: 0.875rem;
	color: #718096;
	margin: 0;
	font-weight: 500;
}

.financial-card .card-content-wrapper {
	padding: 2rem;
}

.financial-content .card-title {
	color: white;
}

.financial-content .card-subtitle {
	color: rgba(255, 255, 255, 0.8);
}

/* Tarjetas de información */
.info-card {
	background: white;
}

.info-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
}

.info-icon-wrapper {
	width: 48px;
	height: 48px;
	border-radius: 12px;
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.3s ease;
}

.package-card:hover .info-icon-wrapper {
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
	transform: scale(1.1);
}

.info-icon {
	color: #ffffff;
	font-size: 1.5rem;
}

.info-value {
	font-size: 1.25rem;
	font-weight: 700;
	color: #1a202c;
	letter-spacing: -0.3px;
}

/* Animaciones */
@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes fadeInScale {
	from {
		opacity: 0;
		transform: scale(0.9);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

@keyframes slideInLeft {
	from {
		opacity: 0;
		transform: translateX(-20px);
	}
	to {
		opacity: 1;
		transform: translateX(0);
	}
}

@keyframes pulse {
	0%,
	100% {
		transform: scale(1);
	}
	50% {
		transform: scale(1.1);
	}
}

@keyframes shine {
	0% {
		transform: translateX(-100%) translateY(-100%) rotate(45deg);
	}
	100% {
		transform: translateX(100%) translateY(100%) rotate(45deg);
	}
}

@keyframes float {
	0%,
	100% {
		transform: translateY(0);
	}
	50% {
		transform: translateY(-10px);
	}
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

/* Responsive */
@media (max-width: 1024px) {
	.package-grid {
		grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
		gap: 1.25rem;
	}
}

@media (max-width: 768px) {
	.package-grid {
		grid-template-columns: 1fr;
		gap: 1rem;
	}
}

@media (max-width: 480px) {
	.card-content-wrapper {
		padding: 1.25rem;
	}
}
</style>
